import * as Yup from 'yup';
import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { Link, Stack, Alert, IconButton, InputAdornment } from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import Iconify from '../../../components/Iconify';
import { FormProvider, RHFCheckbox, RHFTextField } from '../../../components/hook-form';
import { PATH_AUTH } from '../../../routes/path';
import { LogoutReason, takeLogoutReason } from '../../../utils/sessionIdle';
import { accessRefusedMessage } from '../../../utils/accessRefused';

// ----------------------------------------------------------------------

type FormValuesProps = {
  email: string;
  password: string;
  remember: boolean;
  afterSubmit?: string;
  otpCode?: string;
  useRecoveryCode?: boolean;
};

export default function LoginForm() {
  const { login } = useAuth();

  const isMountedRef = useIsMountedRef();

  const [showPassword, setShowPassword] = useState(false);
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [notice, setNotice] = useState<'setupRequired' | 'passwordExpired' | null>(null);
  // Why the user was last signed out (read once, then cleared).
  const [logoutReason] = useState<LogoutReason | null>(() => takeLogoutReason());
  const signedOutIdle = logoutReason === 'idle';
  // Signed out because KYP staff suspended the client or blocked the account.
  const signedOutRefused =
    logoutReason === 'suspended' || logoutReason === 'blocked' ? logoutReason : null;

  const LoginSchema = Yup.object().shape({
    email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
    otpCode: showOtpInput
      ? Yup.string().required('Enter the code from your authenticator app')
      : Yup.string(),
    useRecoveryCode: Yup.boolean(),
  });

  const defaultValues = {};

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(LoginSchema),
    defaultValues,
  });

  const {
    setError,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = methods;

  const onSubmit = async (data: FormValuesProps) => {
    try {
      setNotice(null);
      const result = await login(data.email, data.password, data.otpCode, data.useRecoveryCode);
      if (result.accessRefused) setError('afterSubmit', { message: result.accessRefused });
      if (result.twoFactorRequired) setShowOtpInput(true);
      if (result.twoFactorSetupRequired) setNotice('setupRequired');
      if (result.passwordExpired) setNotice('passwordExpired');
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);

      if (isMountedRef.current) {
        setError('afterSubmit', { ...error, message: error.message });
      }
    }
  };

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={3}>
        {!!errors.afterSubmit && <Alert severity="error">{errors.afterSubmit.message}</Alert>}
        {signedOutRefused && !errors.afterSubmit && (
          <Alert severity="error">{accessRefusedMessage(signedOutRefused)}</Alert>
        )}
        {signedOutIdle && !notice && !showOtpInput && (
          <Alert severity="info">
            You were signed out after a period of inactivity. Sign in again to continue.
          </Alert>
        )}
        {notice === 'setupRequired' && (
          <Alert severity="warning">
            Two-factor authentication is required for your account. Sign in to the KYP platform
            portal to set it up, then sign in here with your authenticator code.
          </Alert>
        )}
        {notice === 'passwordExpired' && (
          <Alert severity="warning">
            Your password has expired.{' '}
            <Link component={RouterLink} to={PATH_AUTH.resetPassword}>
              Reset your password
            </Link>{' '}
            to sign in again.
          </Alert>
        )}

        {showOtpInput && (
          <>
            <RHFTextField
              name="otpCode"
              placeholder="Authentication code"
              autoFocus
              autoComplete="one-time-code"
            />
            <RHFCheckbox name="useRecoveryCode" label="Use a recovery code instead" />
          </>
        )}

        <Stack spacing={3} sx={{ display: showOtpInput ? 'none' : 'flex' }}>
          <RHFTextField name="email" placeholder="Email address" />

          <RHFTextField
            name="password"
            placeholder="Password"
            type={showPassword ? 'text' : 'password'}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                    <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Stack>
      </Stack>

      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ my: 2 }}>
        <Link component={RouterLink} variant="subtitle2" to={PATH_AUTH.resetPassword}>
          Forgot password?
        </Link>
      </Stack>

      <LoadingButton
        fullWidth
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
      >
        Login
      </LoadingButton>
    </FormProvider>
  );
}
